import { useEffect, useRef, useState } from 'react';

// Measures its own rendered width via ResizeObserver and hands (width, height)
// to a render-prop child, so SVG charts get real pixel dimensions without a
// hard-coded width. Falls back to `fallbackWidth` when measurement is
// unavailable (SSR / jsdom / no ResizeObserver) so charts still render
// deterministically in tests and on first paint. Height is fixed by the caller.
export default function ResponsiveContainer({ height = 120, fallbackWidth = 600, className, children }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const w = width > 0 ? width : fallbackWidth;
  return (
    // T334 — `minWidth: 0` and `overflow: hidden` are load-bearing, not tidying.
    //
    // Before the first measure this renders an <svg width={fallbackWidth}>, and
    // an SVG with an explicit width is a replaced element with that intrinsic
    // width. Nothing between it and the surrounding flex item broke min-content
    // propagation, so the item could not shrink below 600px — and the observer
    // then measured 600 and latched there, making the fallback a permanent
    // floor rather than a first guess.
    //
    // Visible on the Trends card between roughly 1024 and 1150px: the sparkline
    // claimed 600px it does not need and crushed the streak column beside it.
    // `minWidth: 0` stops the SVG widening its own parent; `overflow: hidden`
    // clips the single pre-measure frame instead of letting it overlap.
    <div ref={ref} className={className} style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      {children(w, height)}
    </div>
  );
}
